import * as XLSX from "xlsx";

export const QUESTION_SPREADSHEET_COLUMNS = [
  { key: "subject", label: "Subject", required: true, example: "Polity" },
  { key: "topic", label: "Topic", required: true, example: "Preamble & Fundamental Rights" },
  { key: "subtopic", label: "Subtopic", required: false, example: "Right to Equality" },
  { key: "question", label: "Question Text", required: true, example: "Which of the following is not a Fundamental Right?" },
  { key: "option_a", label: "Option A", required: true, example: "Right to Equality" },
  { key: "option_b", label: "Option B", required: true, example: "Right to Freedom" },
  { key: "option_c", label: "Option C", required: true, example: "Right to Property" },
  { key: "option_d", label: "Option D", required: true, example: "Right to Constitutional Remedies" },
  { key: "correct_answer", label: "Correct Option", required: true, example: "C" },
  { key: "explanation", label: "Explanation", required: true, example: "The Right to Property was removed from the list of Fundamental Rights by the 44th Constitutional Amendment Act, 1978." },
  { key: "difficulty", label: "Difficulty", required: true, example: "Easy" },
  { key: "pyq_year", label: "PYQ Year", required: false, example: "2019" },
  { key: "marks", label: "Marks", required: false, example: "2" },
  { key: "negative_marks", label: "Negative Marks", required: false, example: "0.66" },
];

/**
 * Generates sample rows for download templates
 */
export const getSampleTemplateData = () => {
  return [
    {
      "Subject": "Polity",
      "Topic": "Preamble & Fundamental Rights",
      "Subtopic": "Right to Equality",
      "Question Text": "Under which Article of the Indian Constitution is Untouchability abolished?",
      "Option A": "Article 15",
      "Option B": "Article 16",
      "Option C": "Article 17",
      "Option D": "Article 18",
      "Correct Option": "C",
      "Explanation": "Article 17 of the Indian Constitution abolishes 'Untouchability' and forbids its practice in any form.",
      "Difficulty": "Easy",
      "PYQ Year": "2020",
      "Marks": "2",
      "Negative Marks": "0.66"
    },
    {
      "Subject": "History",
      "Topic": "Modern History",
      "Subtopic": "Quit India Movement",
      "Question Text": "Who gave the slogan 'Do or Die' during the Quit India Movement?",
      "Option A": "Subhash Chandra Bose",
      "Option B": "Mahatma Gandhi",
      "Option C": "Jawaharlal Nehru",
      "Option D": "Sardar Vallabhbhai Patel",
      "Correct Option": "B",
      "Explanation": "Mahatma Gandhi gave the mantra 'Do or Die' in his speech delivered at the Gowalia Tank Maidan in Bombay.",
      "Difficulty": "Medium",
      "PYQ Year": "2021",
      "Marks": "2",
      "Negative Marks": "0.66"
    }
  ];
};

/**
 * Downloads a template in CSV format
 */
export const downloadCsvTemplate = () => {
  const data = getSampleTemplateData();
  const headers = Object.keys(data[0]);
  const rows = data.map(item => headers.map(h => `"${String(item[h] || "").replace(/"/g, '""')}"`).join(","));
  const csvContent = [headers.join(","), ...rows].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "upsc_question_bank_template.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Downloads a template in Excel (.xlsx) format
 */
export const downloadExcelTemplate = () => {
  const data = getSampleTemplateData();
  const worksheet = XLSX.utils.json_to_sheet(data);

  // Set column widths
  const colWidths = [
    { wch: 15 }, // Subject
    { wch: 25 }, // Topic
    { wch: 20 }, // Subtopic
    { wch: 45 }, // Question
    { wch: 25 }, // Opt A
    { wch: 25 }, // Opt B
    { wch: 25 }, // Opt C
    { wch: 25 }, // Opt D
    { wch: 15 }, // Correct
    { wch: 45 }, // Explanation
    { wch: 12 }, // Difficulty
    { wch: 12 }, // PYQ Year
    { wch: 10 }, // Marks
    { wch: 15 }, // Negative Marks
  ];
  worksheet["!cols"] = colWidths;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Questions");
  XLSX.writeFile(workbook, "upsc_question_bank_template.xlsx");
};

/**
 * Normalize header string to match standard keys
 */
const normalizeHeader = (header) => {
  const h = String(header || "").toLowerCase().trim().replace(/[^a-z0-9]/g, "");
  if (h.includes("subject")) return "subject";
  if (h.includes("subtopic")) return "subtopic";
  if (h.includes("topic")) return "topic";
  if (h.includes("question")) return "question";
  if (h.includes("optiona") || h === "a") return "option_a";
  if (h.includes("optionb") || h === "b") return "option_b";
  if (h.includes("optionc") || h === "c") return "option_c";
  if (h.includes("optiond") || h === "d") return "option_d";
  if (h.includes("correct") || h.includes("answer")) return "correct_answer";
  if (h.includes("explanation") || h.includes("solution")) return "explanation";
  if (h.includes("difficulty") || h.includes("level")) return "difficulty";
  if (h.includes("pyq") || h.includes("year")) return "pyq_year";
  if (h.includes("negative")) return "negative_marks";
  if (h.includes("mark")) return "marks";
  return null;
};

/**
 * Parses an uploaded file (CSV or Excel) and validates rows
 * @param {File} file
 * @returns {Promise<{ validQuestions: Array, errors: Array, totalRows: number }>}
 */
export const parseQuestionsSpreadsheet = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rawRows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (!rawRows || rawRows.length < 2) {
          resolve({
            validQuestions: [],
            errors: [{ row: 0, message: "File contains no data or is missing headers." }],
            totalRows: 0,
          });
          return;
        }

        const rawHeaders = rawRows[0];
        const headerMap = {};
        rawHeaders.forEach((colName, index) => {
          const key = normalizeHeader(colName);
          if (key) {
            headerMap[index] = key;
          }
        });

        const validQuestions = [];
        const errors = [];
        const totalRows = rawRows.length - 1;

        for (let i = 1; i < rawRows.length; i++) {
          const rowData = rawRows[i];
          if (!rowData || rowData.length === 0 || rowData.every(c => c === "" || c === null || c === undefined)) {
            continue; // Skip empty row
          }

          const qObj = {
            options: {},
          };

          Object.entries(headerMap).forEach(([idx, key]) => {
            const val = rowData[idx];
            const cleanVal = val !== undefined && val !== null ? String(val).trim() : "";
            if (key.startsWith("option_")) {
              const optKey = key.replace("option_", "").toUpperCase();
              qObj.options[optKey] = cleanVal;
            } else {
              qObj[key] = cleanVal;
            }
          });

          // Row validations
          const rowErrors = [];
          const rowNumber = i + 1;

          if (!qObj.subject) rowErrors.push("Subject is required");
          if (!qObj.topic) rowErrors.push("Topic is required");
          if (!qObj.question) rowErrors.push("Question text is required");
          if (!qObj.options.A) rowErrors.push("Option A is required");
          if (!qObj.options.B) rowErrors.push("Option B is required");
          if (!qObj.options.C) rowErrors.push("Option C is required");
          if (!qObj.options.D) rowErrors.push("Option D is required");

          // Validate correct answer
          let correctAns = (qObj.correct_answer || "").toUpperCase().trim();
          if (!["A", "B", "C", "D"].includes(correctAns)) {
            // Check if full text matched
            if (correctAns === qObj.options.A) correctAns = "A";
            else if (correctAns === qObj.options.B) correctAns = "B";
            else if (correctAns === qObj.options.C) correctAns = "C";
            else if (correctAns === qObj.options.D) correctAns = "D";
            else {
              rowErrors.push("Correct Option must be one of: A, B, C, D");
            }
          }
          qObj.correct_answer = correctAns;

          // Normalize difficulty
          let diff = (qObj.difficulty || "Medium").toLowerCase();
          if (diff.includes("easy")) diff = "Easy";
          else if (diff.includes("hard") || diff.includes("difficult")) diff = "Hard";
          else diff = "Medium";
          qObj.difficulty = diff;

          qObj.marks = parseFloat(qObj.marks) || 2;
          qObj.negative_marks = parseFloat(qObj.negative_marks) || 0.66;
          qObj.pyq_year = qObj.pyq_year ? String(qObj.pyq_year) : null;
          qObj.subtopic = qObj.subtopic || "";
          qObj.explanation = qObj.explanation || "No explanation provided.";

          if (rowErrors.length > 0) {
            errors.push({
              row: rowNumber,
              questionPreview: qObj.question || "(Empty question)",
              message: rowErrors.join(", "),
            });
          } else {
            validQuestions.push(qObj);
          }
        }

        resolve({ validQuestions, errors, totalRows });
      } catch (err) {
        console.error("Spreadsheet parsing failed:", err);
        reject(new Error("Failed to parse file. Please verify CSV/Excel format."));
      }
    };

    reader.onerror = () => reject(new Error("Unable to read file."));
    reader.readAsArrayBuffer(file);
  });
};
